import { fetchPublicConfig } from '../api/client';
import { MESSAGES, type AppLocale } from '../i18n/messages';
import { formatCount } from '../i18n/plural';

/**
 * Текст приглашения, который BOSS копирует новому человеку (PRIV-2).
 *
 * <p>Раньше там стояло «сообщения обрабатываются на устройстве до отправки» — читалось как
 * шифрование, которого в обычных чатах нет, — и «около 5 суток», зашитое в клиент и
 * расходившееся с настройкой сервера. Теперь фразы про устройство нет, а срок очистки личных
 * чатов берётся с сервера ({@code GET /api/public/config → directRetentionDays}).
 *
 * @param retentionDays раз во сколько суток стирается история личных чатов: число — подставить,
 *     {@code 0} — очистка выключена, и пункт убирается целиком, {@code null} — сервер не сказал
 *     (старый сервер, нет сети): пункт остаётся, но без числа.
 */
export function buildBossInviteCopyText(
  locale: AppLocale,
  login: string,
  password: string,
  retentionDays: number | null,
): string {
  const m = MESSAGES[locale];
  let retention: string;
  if (retentionDays === 0) {
    retention = '';
  } else if (retentionDays == null) {
    retention = m.bossInviteRetentionPeriodic;
  } else {
    retention = formatCount(m.bossInviteRetentionEvery, locale, 'days', retentionDays);
  }
  return m.bossInviteCopyBody
    .replace(/\{login}/g, login)
    .replace(/\{password}/g, password)
    .replace('{retention}', retention);
}

let pending: Promise<number | null> | null = null;

/**
 * Срок очистки личных чатов с сервера. Запрос — один на запуск: настройка меняется только
 * перезапуском сервера. Неудача не запоминается — следующий вызов спросит снова.
 */
export function loadDirectRetentionDays(): Promise<number | null> {
  if (!pending) {
    pending = fetchPublicConfig().then((cfg) => {
      const days = cfg?.directRetentionDays;
      if (typeof days !== 'number' || !Number.isFinite(days) || days < 0) {
        pending = null;
        return null;
      }
      return Math.floor(days);
    });
  }
  return pending;
}

/** Для тестов: забыть запомненный ответ. */
export function resetDirectRetentionDaysForTests(): void {
  pending = null;
}
